import { reactive } from "vue";

const STORAGE_KEY = "datamind_lang";

// Países de habla hispana / Latinoamérica -> español. El resto -> inglés.
const SPANISH_COUNTRIES = [
  "AR", // Argentina
  "BO", // Bolivia
  "BR", // Brasil (Latinoamérica)
  "CL", // Chile
  "CO", // Colombia
  "CR", // Costa Rica
  "CU", // Cuba
  "DO", // República Dominicana
  "EC", // Ecuador
  "SV", // El Salvador
  "GT", // Guatemala
  "HN", // Honduras
  "MX", // México
  "NI", // Nicaragua
  "PA", // Panamá
  "PY", // Paraguay
  "PE", // Perú
  "PR", // Puerto Rico
  "ES", // España
  "UY", // Uruguay
  "VE", // Venezuela
  "GQ", // Guinea Ecuatorial
];

export const messages = {
  es: {
    nav: {
      home: "Home",
      services: "Servicios",
      projects: "Proyectos",
      contact: "Contacto",
      support: "Soporte",
    },
    home: {
      tagline1: "Tu",
      taglineBold1: "aliado",
      tagline2: "en",
      taglineBold2: "Inteligencia Artificial",
      desc: "Impulsamos a tu empresa con soluciones de software, datos e inteligencia artificial diseñadas a la medida de tus objetivos.",
      btnServices: "Ver Servicios",
      btnContact: "Contáctanos",
    },
    slider: {
      items: [
        {
          img: "img/slider/4.png",
          tag: "Desarrollo",
          title: "Software y apps a la medida",
          des: "Construimos aplicaciones web y móviles que se adaptan exactamente a los procesos de tu empresa.",
        },
        {
          img: "img/slider/3.png",
          tag: "Analítica IA",
          title: "Tus datos, decisiones claras",
          des: "Convertimos grandes volúmenes de información en insights accionables para decidir con evidencia.",
        },
        {
          img: "img/slider/1.png",
          tag: "Infraestructura · RFID",
          title: "Operación inteligente y trazable",
          des: "Infraestructura escalable y control de inventario en tiempo real con etiquetas RFID.",
        },
      ],
    },
    services: {
      titleLight: "Nuestros",
      titleColored: "Servicios",
      subtitle:
        "Soluciones tecnológicas e inteligencia artificial de extremo a extremo para tu empresa.",
      items: [
        {
          title: "Desarrollo de Software a la Medida",
          icon: "icon-code",
          des: "Diseñamos y construimos aplicaciones de software personalizadas que se adaptan exactamente a los procesos y objetivos de tu empresa, garantizando escalabilidad, seguridad y eficiencia.",
        },
        {
          title: "Desarrollo de App",
          icon: "icon-mobile-alt",
          des: "Creamos aplicaciones móviles nativas e híbridas para iOS y Android, con experiencias de usuario intuitivas y un rendimiento óptimo en cualquier dispositivo.",
        },
        {
          title: "Entrenamiento de Modelos IA",
          icon: "icon-data-science",
          des: "Entrenamos y ajustamos modelos de inteligencia artificial y machine learning con tus datos para automatizar tareas, generar predicciones precisas y crear soluciones inteligentes.",
        },
        {
          title: "Perfilamiento de Datos IA",
          icon: "icon-database",
          des: "Analizamos, clasificamos y enriquecemos tus datos mediante IA para descubrir patrones, segmentar perfiles y elevar la calidad de tu información.",
        },
        {
          title: "Análisis de Datos IA",
          icon: "icon-chart-line-1",
          des: "Transformamos grandes volúmenes de datos en insights accionables con análisis avanzado e inteligencia artificial, impulsando decisiones estratégicas basadas en evidencia.",
        },
        {
          title: "Infraestructura Empresarial Inteligente",
          icon: "icon-network",
          des: "Implementamos infraestructura tecnológica escalable y automatizada —cloud, redes y servidores— optimizada con IA para una operación empresarial confiable y de alto rendimiento.",
        },
        {
          title: "Consultorías",
          icon: "icon-lightbulb",
          des: "Te asesoramos en el desarrollo de software y en la implementación estratégica de inteligencia artificial en tu empresa, definiendo la hoja de ruta tecnológica que maximiza el retorno de tu inversión.",
        },
      ],
    },
    projects: {
      titleLight: "Nuestros",
      titleColored: "Proyectos",
      subtitle:
        "Soluciones reales que hemos desarrollado e implementado para transformar la operación de las empresas.",
      refsLabel: "Referencias",
      modal: {
        viewDetail: "Ver detalle",
        close: "Cerrar",
        overview: "Descripción general",
        highlights: "Características principales",
        modules: "Módulos",
        cta: "Solicitar una demo",
      },
      items: [
        {
          title: "SmartGov 360 ERP",
          tag: "ERP · Gobierno",
          img: "img/projects/smartgov-360-erp.png",
          logo: "img/projects/smartgov-360-logo.jpg",
          des: "ERP diseñado para instituciones de gobierno que integra en un solo espacio de trabajo la gestión administrativa, financiera y operativa, con control de pendientes, trazabilidad de actividad y consola de seguridad, usuarios y auditoría.",
          modulesLabel: "Módulos",
          modules: [
            "Gestión Patrimonial",
            "Gestión Documental",
            "Gestión de Personal",
            "Contabilidad",
            "Presupuesto",
            "Finanzas",
            "Control Vehicular",
            "UCP – Compras",
            "Cobros de Servicios",
            "Contratos y Proyectos",
          ],
          refs: [{ label: "Caso de éxito", url: "#" }],
          detail: {
            subtitle: "ERP para instituciones de gobierno",
            overview:
              "SmartGov 360 es una plataforma ERP pensada para la administración pública. Reúne en un escritorio único las aplicaciones institucionales, los pendientes de cada usuario y la actividad reciente, con control de acceso por roles y trazabilidad completa para auditoría.",
            stats: [
              { value: "10", label: "Módulos institucionales" },
              { value: "360°", label: "Visión de la operación" },
              { value: "100%", label: "Trazabilidad y auditoría" },
            ],
            gallery: [
              {
                img: "img/projects/smartgov-360-erp.png",
                caption: "Escritorio ERP con las aplicaciones institucionales",
              },
              {
                img: "img/projects/smartgov-360-patrimonial.png",
                caption: "Panel General del módulo de Gestión Patrimonial",
              },
            ],
            highlights: [
              "Escritorio único con acceso a todas las aplicaciones y búsqueda global",
              "Bandeja de pendientes: firmas, traslados, recepciones y conciliaciones",
              "Indicadores en tiempo real por módulo",
              "Historial de movimientos con estados y trazabilidad",
              "Consola de gobierno: seguridad, usuarios y auditoría",
              "Roles y permisos por dependencia",
            ],
            modules: [
              { name: "Gestión Patrimonial", des: "Bienes y ciclo patrimonial institucional" },
              { name: "Gestión Documental", des: "Expedientes y evidencia patrimonial" },
              { name: "Gestión de Personal", des: "Personas y estructura institucional" },
              { name: "Contabilidad", des: "Gestión y control contable institucional" },
              { name: "Presupuesto", des: "Planificación y control financiero" },
              { name: "Finanzas", des: "Tesorería y flujos institucionales" },
              { name: "Control Vehicular", des: "Flota y logística vehicular institucional" },
              { name: "UCP – Compras", des: "Procesos y compras públicas" },
              { name: "Cobros de Servicios", des: "Recaudación y aranceles registrales" },
              { name: "Contratos y Proyectos", des: "Supervisión de contratos e hitos" },
            ],
            featured: {
              title: "Módulo destacado: Gestión Patrimonial",
              des: "Control del ciclo de vida de los bienes del Estado, desde el alta hasta el descargo, con conciliación contable y verificación física.",
              items: [
                "Maestro integral de bienes, componentes e historial",
                "Verificación física e inventario",
                "Responsabilidad y asignación de bienes",
                "Bodega, custodia y condición",
                "Operaciones especiales: traslados y descargos",
                "Depreciación y conciliación contable",
                "Catálogos patrimoniales, reportería y auditoría",
              ],
            },
          },
        },
        {
          title: "Athletix ERP",
          tag: "ERP · Federaciones deportivas",
          img: "img/projects/athletix-erp.png",
          logo: "img/projects/athletix-logo.jpg",
          des: "ERP para federaciones deportivas que centraliza la gestión federativa, clubes, atletas, competencias y resultados, junto con la administración financiera, por temporada y con consola de usuarios, permisos y auditoría.",
          modulesLabel: "Módulos",
          modules: [
            "Gestión Federativa",
            "Clubes y Afiliación",
            "Atletas y Responsables",
            "Gestión Deportiva",
            "Competencias",
            "Resultados, Rankings y Récords",
            "Finanzas y Administración",
            "Compras, Contratos y Proyectos",
            "Patrimonio, Activos y Logística",
          ],
          refs: [{ label: "Caso de éxito", url: "#" }],
          detail: {
            subtitle: "ERP para federaciones deportivas",
            overview:
              "Athletix ERP es una plataforma pensada para federaciones deportivas. Reúne en un escritorio único las aplicaciones de la federación, organizadas por temporada, con los pendientes de cada usuario, la actividad reciente y una consola federativa para personas, usuarios, perfiles, permisos, seguridad y auditoría.",
            stats: [
              { value: "9", label: "Aplicaciones federativas" },
              { value: "7", label: "Módulos habilitados" },
              { value: "2", label: "Módulos en roadmap" },
            ],
            gallery: [
              {
                img: "img/projects/athletix-erp.png",
                caption: "Escritorio de aplicaciones del ERP federativo",
              },
            ],
            highlights: [
              "Escritorio único con las aplicaciones de la federación",
              "Operación organizada por temporada",
              "Perfil longitudinal de atletas con vínculos y documentos",
              "Resultados, rankings y récords históricos",
              "Bandeja de pendientes: afiliaciones, certificaciones y validaciones",
              "Consola federativa: usuarios, permisos, seguridad y auditoría",
            ],
            modules: [
              { name: "Gestión Federativa", des: "Federación, temporadas, directorio, documentos y portal institucional" },
              { name: "Clubes y Afiliación", des: "Clubes, renovaciones, sedes, directiva y expedientes" },
              { name: "Atletas y Responsables", des: "Registro, perfiles longitudinales, vínculos y documentos" },
              { name: "Gestión Deportiva", des: "Equipo técnico, grupos, planificación, sesiones y programas" },
              { name: "Competencias", des: "Calendario, inscripciones, programa, oficiales y operación" },
              { name: "Resultados, Rankings y Récords", des: "Resultados, puntos, rankings, récords e histórico" },
              { name: "Finanzas y Administración", des: "Cobros, pagos, solvencia y conciliación" },
              { name: "Compras, Contratos y Proyectos", des: "Proveedores, adquisiciones, contratos, convenios y proyectos" },
              { name: "Patrimonio, Activos y Logística", des: "Bienes, inventario, activos de sedes y logística" },
            ],
            featured: {
              title: "Módulos destacados: Competencias y Resultados",
              des: "Todo el ciclo de una competencia, desde el calendario hasta los récords, en un mismo flujo.",
              items: [
                "Calendario de competencias e inscripciones",
                "Programa de pruebas y asignación de oficiales",
                "Operación de la competencia",
                "Registro de resultados y puntuación",
                "Rankings por categoría",
                "Récords por piscina (25 y 50 m) e histórico",
              ],
            },
          },
        },
        {
          title: "ERP Comercial",
          tag: "ERP · Pymes",
          img: "img/projects/erp-pymes.png",
          des: "ERP para pequeñas y medianas empresas que integra facturación electrónica (DTE), inventario, compras, contabilidad, CRM y activos fijos en una sola plataforma, con Sofia, una asistente de IA que responde preguntas sobre el negocio.",
          modulesLabel: "Módulos",
          modules: [
            "Facturación",
            "Inventario",
            "Restaurante",
            "Contabilidad",
            "Compras",
            "CRM",
            "Activos Fijos",
            "Configuraciones",
          ],
          refs: [{ label: "Caso de éxito", url: "#" }],
          detail: {
            subtitle: "ERP para pequeñas y medianas empresas, con asistente de IA",
            overview:
              "Una plataforma comercial pensada para pymes y medianas empresas: vende, factura electrónicamente, controla inventario y compras, lleva la contabilidad y da seguimiento a clientes desde un mismo lugar. Maneja varias empresas desde un solo acceso e incluye a Sofia, una asistente de IA que cruza la información de los módulos para responder preguntas del negocio en lenguaje natural.",
            stats: [
              { value: "8", label: "Aplicaciones integradas" },
              { value: "DTE", label: "Facturación electrónica" },
              { value: "IA", label: "Asistente Sofia integrada" },
            ],
            gallery: [
              {
                img: "img/projects/erp-pymes.png",
                caption: "Escritorio de aplicaciones con la asistente Sofia",
              },
            ],
            audienceTitle: "Ideal para",
            audience: [
              "Pequeñas empresas",
              "Medianas empresas",
              "Comercios y distribuidoras",
              "Restaurantes",
              "Empresas de servicios",
            ],
            highlights: [
              "Facturación electrónica (DTE) y punto de venta",
              "Varias empresas desde un solo acceso",
              "Contabilidad de partida doble integrada a ventas y compras",
              "CRM con pipeline visual de oportunidades",
              "Usuarios y perfiles de permisos por empresa",
              "Asistente de IA con consultas por texto o voz",
            ],
            modules: [
              { name: "Facturación", des: "POS, DTE, contingencias, cuentas por cobrar y reportes" },
              { name: "Inventario", des: "Productos, bodegas, kárdex, traslados, mermas y levantamiento físico" },
              { name: "Restaurante", des: "Pedidos por mesa, vista de cocina y enlace al DTE" },
              { name: "Contabilidad", des: "Plan de cuentas, partida doble, Diario, Mayor y Balance" },
              { name: "Compras", des: "Órdenes de compra, recepción, facturas de proveedor y CxP" },
              { name: "CRM", des: "Oportunidades, leads, conversión, tareas y dashboard comercial" },
              { name: "Activos Fijos", des: "Catálogo, depreciación mensual, bajas y reportes" },
              { name: "Configuraciones", des: "Empresas, usuarios, perfiles de permisos y actividad económica" },
            ],
            featured: {
              title: "Sofia · Asistente de IA",
              des: "Sofia conoce los módulos a los que tiene acceso cada usuario y responde preguntas cruzando ventas, CRM, cuentas por cobrar y contabilidad.",
              items: [
                "¿Cómo va el negocio este mes?",
                "Oportunidades abiertas vs. facturado del mes",
                "Rentabilidad: ingresos vs. egresos por línea",
                "Top clientes por facturación y pipeline",
                "Cobranza pendiente y flujo de caja proyectado",
                "Centro de comando para administradores",
              ],
            },
          },
        },
        {
          title: "Helpdesk DataMind",
          tag: "Gestión de tickets · Servicios",
          img: "img/projects/helpdesk-inicio.jpg",
          des: "Mesa de servicio para toda la empresa, no solo para TI: gestiona solicitudes de cualquier área (contratos, recursos humanos, finanzas, mantenimiento o clientes) con catálogo de servicios, SLA, control de horas trabajadas y reportes.",
          modulesLabel: "Áreas",
          modules: [
            "TI y sistemas",
            "Contratos y legal",
            "Recursos humanos",
            "Finanzas y compras",
            "Mantenimiento",
            "Atención a clientes",
          ],
          refs: [{ label: "Caso de éxito", url: "#" }],
          detail: {
            subtitle: "Gestión de tickets y servicios para todas las áreas de la empresa",
            overview:
              "Helpdesk DataMind centraliza las solicitudes de la empresa en un solo portal. Cada área publica sus servicios con categorías y formularios propios; los usuarios crean tickets en tres pasos y dan seguimiento a su estado. Los equipos trabajan con prioridades, tiempos de SLA y registro de horas, y la dirección mide volumen, carga, cumplimiento y satisfacción con reportes exportables. Sirve tanto para equipos internos como para atender a organizaciones cliente.",
            stats: [
              { value: "SLA", label: "Asignación, revisión, respuesta y resolución" },
              { value: "Horas", label: "Control del tiempo trabajado por ticket" },
              { value: "CSAT", label: "Encuestas de satisfacción" },
            ],
            gallery: [
              { img: "img/projects/helpdesk-inicio.jpg", caption: "Inicio: tickets por estado, prioridad, SLA y satisfacción" },
              { img: "img/projects/helpdesk-tickets.jpg", caption: "Mis tickets con categoría, prioridad, estado y SLA" },
              { img: "img/projects/helpdesk-nuevo-1.jpg", caption: "Nuevo ticket · paso 1: elegir el servicio" },
              { img: "img/projects/helpdesk-nuevo-2.jpg", caption: "Nuevo ticket · paso 2: clasificar por categoría" },
              { img: "img/projects/helpdesk-nuevo-3.jpg", caption: "Nuevo ticket · paso 3: formulario propio de la categoría" },
              { img: "img/projects/helpdesk-reportes.jpg", caption: "Reportes por estado, prioridad y departamento, exportables a CSV" },
            ],
            audienceTitle: "Para todas las áreas",
            audience: [
              "TI y sistemas",
              "Gestión de contratos",
              "Recursos humanos",
              "Finanzas y compras",
              "Mantenimiento e instalaciones",
              "Atención a clientes",
              "Proyectos y consultoría",
            ],
            highlights: [
              "Catálogo de servicios por área, con categorías y subcategorías",
              "Formularios dinámicos por categoría, con adjuntos",
              "SLA con relojes de asignación, revisión, 1.ª respuesta y resolución",
              "Registro de horas trabajadas y carga por equipo",
              "Portal para organizaciones cliente, cada una con sus tickets",
              "Base de conocimiento para resolver sin abrir un ticket",
            ],
            modulesTitle: "Funcionalidades",
            modules: [
              { name: "Portal de solicitudes", des: "Inicio con indicadores y acceso rápido a nuevo ticket" },
              { name: "Tickets", des: "Seguimiento por estado, prioridad, SLA y fecha" },
              { name: "Catálogo de servicios", des: "Servicios por área con categorías y enrutamiento" },
              { name: "Formularios", des: "Campos propios por categoría y documentos de evidencia" },
              { name: "SLA", des: "Tiempos objetivo y alertas de vencimiento" },
              { name: "Horas trabajadas", des: "Tiempo invertido por ticket, agente y cliente" },
              { name: "Satisfacción", des: "Encuestas CSAT al cerrar el ticket" },
              { name: "Base de conocimiento", des: "Artículos y guías de autoservicio" },
              { name: "Reportes", des: "Volumen, carga, SLA y CSAT por rango de fechas" },
              { name: "Organizaciones", des: "Clientes y departamentos con sus propios usuarios" },
            ],
            featured: {
              title: "Más allá de TI",
              des: "El mismo flujo sirve para cualquier solicitud interna o de clientes. Algunos ejemplos:",
              items: [
                "Contratos: solicitud, revisión y renovación de contratos",
                "Horas: registro y reporte de horas por cliente o proyecto",
                "Recursos humanos: constancias, permisos y altas de personal",
                "Finanzas: aprobaciones de pagos y solicitudes de compra",
                "Mantenimiento: reportes de instalaciones y equipos",
                "Clientes: soporte con SLA acordado por contrato",
              ],
            },
          },
        },
        {
          title: "Aula Virtual",
          tag: "E-learning · Educación",
          img: "img/projects/aula-virtual-curso.png",
          des: "Implementación de aulas virtuales para capacitación y formación en línea: cursos con sesiones en vivo, contenido pregrabado, materiales, actividades y calificaciones, con la marca de cada institución.",
          modulesLabel: "Ideal para",
          modules: [
            "Capacitación empresarial",
            "Formación profesional",
            "Colegios",
            "Universidades",
            "Federaciones deportivas",
          ],
          refs: [{ label: "Caso de éxito", url: "#" }],
          detail: {
            subtitle: "Plataforma de aprendizaje en línea con la marca de tu institución",
            overview:
              "Implementamos y configuramos aulas virtuales listas para operar: personalizamos la plataforma con la identidad de la institución, creamos la estructura de cursos, cargamos contenidos, damos de alta a los participantes y acompañamos al equipo durante la operación. Los participantes acceden desde cualquier dispositivo a sus cursos, calendario, materiales y calificaciones.",
            stats: [
              { value: "96", label: "Participantes en el primer curso de FESADA" },
              { value: "24/7", label: "Acceso a contenidos" },
              { value: "100%", label: "En línea, desde cualquier dispositivo" },
            ],
            gallery: [
              {
                img: "img/projects/aula-virtual-curso.png",
                caption: "Página de curso con portada, descripción y calendario",
              },
              {
                img: "img/projects/aula-virtual-dashboard.png",
                caption: "Panel del participante con sus cursos y actividad reciente",
              },
              {
                img: "img/projects/aula-virtual-login.png",
                caption: "Acceso personalizado con la marca de la institución",
              },
            ],
            audienceTitle: "Ideal para",
            audience: [
              "Capacitación empresarial",
              "Formación profesional",
              "Colegios",
              "Universidades",
              "Federaciones deportivas",
            ],
            highlights: [
              "Imagen personalizada: logo, colores y página de acceso propia",
              "Sesiones en vivo y contenido pregrabado",
              "Materiales de apoyo y actividades prácticas",
              "Calificaciones y seguimiento del avance de cada participante",
              "Calendario de sesiones y entregas",
              "Notificaciones, mensajería y modo oscuro",
            ],
            modulesTitle: "Funcionalidades",
            modules: [
              { name: "Cursos", des: "Portada, descripción, secciones y categorías" },
              { name: "Participantes", des: "Inscripción, roles y grupos" },
              { name: "Actividades", des: "Tareas, cuestionarios, foros y recursos" },
              { name: "Calificaciones", des: "Libro de notas y seguimiento del avance" },
              { name: "Calendario", des: "Sesiones, entregas y eventos del curso" },
              { name: "Banco de contenidos", des: "Recursos reutilizables entre cursos" },
              { name: "Archivos", des: "Documentos, videos y presentaciones" },
              { name: "Mensajería", des: "Notificaciones y mensajes internos" },
              { name: "Panel del participante", des: "Cursos, avance y elementos recientes" },
              { name: "Administración", des: "Usuarios, permisos, apariencia y reportes" },
            ],
            featured: {
              title: "Caso: FESADA – Curso de Educadores Acuáticos",
              des: "Para la Federación Salvadoreña de Deportes Acuáticos (FESADA), con el apoyo del INDES, implementamos el aula virtual del Curso de Educadores Acuáticos – Online N.° 1 El Salvador 2026.",
              items: [
                "Aula con la marca de FESADA y acceso propio",
                "96 participantes inscritos en el primer curso",
                "Sesiones en vivo y contenido pregrabado",
                "Fundamentos psicopedagógicos y técnicas de natación",
                "Materiales de apoyo y actividades prácticas",
                "Calendario de sesiones del programa",
              ],
            },
          },
        },
        {
          title: "ERP Inteligente",
          tag: "Software · IA",
          img: "img/projects/erp-inteligente.png",
          des: "Sistema de planificación de recursos empresariales potenciado con IA que integra finanzas, inventario, ventas y operaciones, con predicciones y automatización inteligente de procesos.",
          refs: [{ label: "Caso de éxito", url: "#" }],
        },
        {
          title: "Agente Multicanal",
          tag: "IA Conversacional",
          img: "img/projects/agente-multicanal.png",
          des: "Asistente conversacional con IA que atiende a tus clientes de forma unificada por WhatsApp, web, redes sociales y correo, disponible 24/7 y con respuestas contextuales.",
          refs: [{ label: "Demo", url: "#" }],
        },
        {
          title: "Perfilamiento IA",
          tag: "Data · IA",
          img: "img/projects/perfilamiento-ia.png",
          des: "Plataforma que analiza y segmenta perfiles de clientes y usuarios mediante inteligencia artificial para personalizar la oferta y anticipar comportamientos.",
          refs: [{ label: "Ficha técnica", url: "#" }],
        },
        {
          title: "Gestión de Activo Fijo con RFID",
          tag: "RFID · Software Empresarial",
          img: "img/projects/activo-fijo-rfid.png",
          des: "Control y trazabilidad del ciclo de vida de los activos de la empresa con monitoreo de inventario en tiempo real mediante etiquetas RFID: altas, ubicación, conteos automáticos, depreciación, mantenimiento y bajas, reduciendo mermas y errores operativos.",
          refs: [{ label: "Caso de éxito", url: "#" }],
        },
      ],
    },
    contact: {
      titleLight: "Hablemos de tu",
      titleColored: "Proyecto",
      address:
        "Avenida El Espino y Blvd Orden de Malta, Edificio Holcim Nivel 3 Oficina 4, Urb. Madre Selva III",
      namePlaceholder: "Nombre",
      emailPlaceholder: "Correo",
      messagePlaceholder: "Mensaje",
      send: "Enviar Mensaje",
      sending: "Enviando...",
      success:
        "Hemos recibido tu mensaje, te contactaremos pronto.",
      required: "Por favor completa los campos requeridos",
      invalidEmail: "Por favor escribe un correo válido",
      error:
        "No pudimos enviar tu mensaje. Escríbenos a ventas.sv@datamindss.com o por WhatsApp.",
      rateLimit: "Has enviado varios mensajes. Espera un momento e intenta de nuevo.",
      whatsapp: "Escríbenos por WhatsApp",
      whatsappNumber: "+503 7468 9732",
      whatsappMessage:
        "¡Hola! Vengo del sitio web de DataMind y quiero información sobre sus servicios.",
    },
    footer: {
      tagline:
        "Soluciones empresariales de software, inteligencia artificial, datos, infraestructura y RFID.",
      addressShort: "Edificio Holcim, Nivel 3 Oficina 4 · Urb. Madre Selva III, El Salvador",
      servicesTitle: "Servicios",
      solutionsTitle: "Soluciones",
      companyTitle: "DataMind",
      contactTitle: "Contacto",
      talk: "Hablemos",
      ctaTitle: "¿Tienes un proyecto en mente?",
      ctaText: "Conversemos sobre tu proceso, tu información y el resultado que necesitas.",
      ctaButton: "Contactar a DataMind",
      rights: "Todos los derechos reservados.",
      backToTop: "Volver arriba",
    },
    sofia: {
      bubble: "¿Hablamos?",
      role: "Asistente de DataMind",
      greeting:
        "¡Hola! Soy Sofía, del equipo de DataMind. ¿En qué le puedo ayudar? Puedo contarle sobre nuestros servicios de software, datos e inteligencia artificial.",
      placeholder: "Escriba su mensaje...",
      fallback: "Gracias por su mensaje, en un momento le respondemos.",
      error: "No pude conectar en este momento. Intente de nuevo.",
      rateLimit:
        "Ha enviado muchos mensajes seguidos. Espere un momento y vuelva a intentarlo.",
    },
  },

  en: {
    nav: {
      home: "Home",
      services: "Services",
      projects: "Projects",
      contact: "Contact",
      support: "Support",
    },
    home: {
      tagline1: "Your",
      taglineBold1: "partner",
      tagline2: "in",
      taglineBold2: "Artificial Intelligence",
      desc: "We empower your business with software, data and artificial intelligence solutions tailored to your goals.",
      btnServices: "View Services",
      btnContact: "Contact Us",
    },
    slider: {
      items: [
        {
          img: "img/slider/4.png",
          tag: "Development",
          title: "Custom software and apps",
          des: "We build web and mobile applications that fit your company's processes exactly.",
        },
        {
          img: "img/slider/3.png",
          tag: "AI Analytics",
          title: "Your data, clear decisions",
          des: "We turn large volumes of information into actionable insights so you decide with evidence.",
        },
        {
          img: "img/slider/1.png",
          tag: "Infrastructure · RFID",
          title: "Smart, traceable operations",
          des: "Scalable infrastructure and real-time inventory control with RFID tags.",
        },
      ],
    },
    services: {
      titleLight: "Our",
      titleColored: "Services",
      subtitle:
        "End-to-end technology and artificial intelligence solutions for your business.",
      items: [
        {
          title: "Custom Software Development",
          icon: "icon-code",
          des: "We design and build custom software applications that fit your company's processes and goals exactly, ensuring scalability, security and efficiency.",
        },
        {
          title: "App Development",
          icon: "icon-mobile-alt",
          des: "We create native and hybrid mobile apps for iOS and Android, with intuitive user experiences and optimal performance on any device.",
        },
        {
          title: "AI Model Training",
          icon: "icon-data-science",
          des: "We train and fine-tune artificial intelligence and machine learning models with your data to automate tasks, generate accurate predictions and build intelligent solutions.",
        },
        {
          title: "AI Data Profiling",
          icon: "icon-database",
          des: "We analyze, classify and enrich your data using AI to uncover patterns, segment profiles and raise the quality of your information.",
        },
        {
          title: "AI Data Analytics",
          icon: "icon-chart-line-1",
          des: "We turn large volumes of data into actionable insights with advanced analytics and artificial intelligence, driving evidence-based strategic decisions.",
        },
        {
          title: "Intelligent Enterprise Infrastructure",
          icon: "icon-network",
          des: "We deploy scalable, automated technology infrastructure — cloud, networks and servers — optimized with AI for reliable, high-performance business operations.",
        },
        {
          title: "Consulting",
          icon: "icon-lightbulb",
          des: "We advise you on software development and the strategic implementation of artificial intelligence in your company, defining the technology roadmap that maximizes your return on investment.",
        },
      ],
    },
    projects: {
      titleLight: "Our",
      titleColored: "Projects",
      subtitle:
        "Real solutions we have built and deployed to transform how companies operate.",
      refsLabel: "References",
      modal: {
        viewDetail: "View details",
        close: "Close",
        overview: "Overview",
        highlights: "Key features",
        modules: "Modules",
        cta: "Request a demo",
      },
      items: [
        {
          title: "SmartGov 360 ERP",
          tag: "ERP · Government",
          img: "img/projects/smartgov-360-erp.png",
          logo: "img/projects/smartgov-360-logo.jpg",
          des: "ERP built for government institutions that brings administrative, financial and operational management into a single workspace, with task tracking, activity traceability and a security, users and audit console.",
          modulesLabel: "Modules",
          modules: [
            "Asset Management",
            "Document Management",
            "Human Resources",
            "Accounting",
            "Budget",
            "Finance",
            "Fleet Control",
            "Procurement (UCP)",
            "Service Billing",
            "Contracts & Projects",
          ],
          refs: [{ label: "Case study", url: "#" }],
          detail: {
            subtitle: "ERP for government institutions",
            overview:
              "SmartGov 360 is an ERP platform designed for public administration. It brings institutional applications, each user's pending tasks and recent activity into a single desktop, with role-based access control and full traceability for audits.",
            stats: [
              { value: "10", label: "Institutional modules" },
              { value: "360°", label: "View of operations" },
              { value: "100%", label: "Traceability and audit" },
            ],
            gallery: [
              {
                img: "img/projects/smartgov-360-erp.png",
                caption: "ERP desktop with the institutional applications",
              },
              {
                img: "img/projects/smartgov-360-patrimonial.png",
                caption: "Overview dashboard of the Asset Management module",
              },
            ],
            highlights: [
              "Single desktop with access to every application and global search",
              "Pending tasks inbox: signatures, transfers, receptions and reconciliations",
              "Real-time indicators per module",
              "Transaction history with statuses and traceability",
              "Government console: security, users and audit",
              "Roles and permissions per department",
            ],
            modules: [
              { name: "Asset Management", des: "Public assets and their lifecycle" },
              { name: "Document Management", des: "Case files and asset evidence" },
              { name: "Human Resources", des: "People and organizational structure" },
              { name: "Accounting", des: "Institutional accounting management and control" },
              { name: "Budget", des: "Financial planning and control" },
              { name: "Finance", des: "Treasury and institutional cash flows" },
              { name: "Fleet Control", des: "Institutional fleet and logistics" },
              { name: "Procurement (UCP)", des: "Public purchasing processes" },
              { name: "Service Billing", des: "Collections and registry fees" },
              { name: "Contracts & Projects", des: "Contract and milestone supervision" },
            ],
            featured: {
              title: "Featured module: Asset Management",
              des: "Lifecycle control of public assets, from registration to disposal, with accounting reconciliation and physical verification.",
              items: [
                "Asset master record, components and history",
                "Physical verification and inventory",
                "Custodianship and asset assignment",
                "Warehouse, custody and condition",
                "Special operations: transfers and disposals",
                "Depreciation and accounting reconciliation",
                "Asset catalogs, reporting and audit",
              ],
            },
          },
        },
        {
          title: "Athletix ERP",
          tag: "ERP · Sports federations",
          img: "img/projects/athletix-erp.png",
          logo: "img/projects/athletix-logo.jpg",
          des: "ERP for sports federations that centralizes federation management, clubs, athletes, competitions and results, along with financial administration, organized by season and with a console for users, permissions and audit.",
          modulesLabel: "Modules",
          modules: [
            "Federation Management",
            "Clubs & Membership",
            "Athletes & Guardians",
            "Sports Management",
            "Competitions",
            "Results, Rankings & Records",
            "Finance & Administration",
            "Procurement, Contracts & Projects",
            "Assets & Logistics",
          ],
          refs: [{ label: "Case study", url: "#" }],
          detail: {
            subtitle: "ERP for sports federations",
            overview:
              "Athletix ERP is a platform designed for sports federations. It brings the federation's applications into a single desktop, organized by season, with each user's pending tasks, recent activity and a federation console for people, users, profiles, permissions, security and audit.",
            stats: [
              { value: "9", label: "Federation applications" },
              { value: "7", label: "Modules enabled" },
              { value: "2", label: "Modules on the roadmap" },
            ],
            gallery: [
              {
                img: "img/projects/athletix-erp.png",
                caption: "Application desktop of the federation ERP",
              },
            ],
            highlights: [
              "Single desktop with all the federation's applications",
              "Operations organized by season",
              "Longitudinal athlete profiles with links and documents",
              "Results, rankings and historical records",
              "Pending tasks inbox: memberships, certifications and validations",
              "Federation console: users, permissions, security and audit",
            ],
            modules: [
              { name: "Federation Management", des: "Federation, seasons, directory, documents and institutional portal" },
              { name: "Clubs & Membership", des: "Clubs, renewals, venues, boards and case files" },
              { name: "Athletes & Guardians", des: "Registration, longitudinal profiles, links and documents" },
              { name: "Sports Management", des: "Coaching staff, groups, planning, sessions and programs" },
              { name: "Competitions", des: "Calendar, entries, program, officials and operations" },
              { name: "Results, Rankings & Records", des: "Results, points, rankings, records and history" },
              { name: "Finance & Administration", des: "Collections, payments, good standing and reconciliation" },
              { name: "Procurement, Contracts & Projects", des: "Suppliers, purchasing, contracts, agreements and projects" },
              { name: "Assets & Logistics", des: "Property, inventory, venue assets and logistics" },
            ],
            featured: {
              title: "Featured modules: Competitions and Results",
              des: "The full competition cycle, from the calendar to the records, in a single flow.",
              items: [
                "Competition calendar and entries",
                "Event program and officials assignment",
                "Competition operations",
                "Results entry and scoring",
                "Rankings by category",
                "Records by pool length (25 and 50 m) and history",
              ],
            },
          },
        },
        {
          title: "Business ERP",
          tag: "ERP · SMBs",
          img: "img/projects/erp-pymes.png",
          des: "ERP for small and medium-sized businesses that brings e-invoicing (DTE), inventory, purchasing, accounting, CRM and fixed assets into one platform, with Sofia, an AI assistant that answers questions about the business.",
          modulesLabel: "Modules",
          modules: [
            "Invoicing",
            "Inventory",
            "Restaurant",
            "Accounting",
            "Purchasing",
            "CRM",
            "Fixed Assets",
            "Settings",
          ],
          refs: [{ label: "Case study", url: "#" }],
          detail: {
            subtitle: "ERP for small and medium-sized businesses, with an AI assistant",
            overview:
              "A business platform designed for small and medium-sized companies: sell, issue electronic invoices, control inventory and purchasing, keep the books and follow up with customers from one place. It manages several companies from a single login and includes Sofia, an AI assistant that combines data across modules to answer business questions in plain language.",
            stats: [
              { value: "8", label: "Integrated applications" },
              { value: "DTE", label: "Electronic invoicing" },
              { value: "AI", label: "Built-in Sofia assistant" },
            ],
            gallery: [
              {
                img: "img/projects/erp-pymes.png",
                caption: "Application desktop with the Sofia assistant",
              },
            ],
            audienceTitle: "Ideal for",
            audience: [
              "Small businesses",
              "Mid-sized companies",
              "Retail and distribution",
              "Restaurants",
              "Service companies",
            ],
            highlights: [
              "Electronic invoicing (DTE) and point of sale",
              "Several companies from a single login",
              "Double-entry accounting linked to sales and purchasing",
              "CRM with a visual opportunity pipeline",
              "Users and permission profiles per company",
              "AI assistant with text or voice queries",
            ],
            modules: [
              { name: "Invoicing", des: "POS, DTE, contingencies, receivables and reports" },
              { name: "Inventory", des: "Products, warehouses, stock ledger, transfers, shrinkage and counts" },
              { name: "Restaurant", des: "Table orders, kitchen view and DTE link" },
              { name: "Accounting", des: "Chart of accounts, double entry, journal, ledger and balance sheet" },
              { name: "Purchasing", des: "Purchase orders, receiving, supplier invoices and payables" },
              { name: "CRM", des: "Opportunities, leads, conversion, tasks and sales dashboard" },
              { name: "Fixed Assets", des: "Catalog, monthly depreciation, disposals and reports" },
              { name: "Settings", des: "Companies, users, permission profiles and business activity" },
            ],
            featured: {
              title: "Sofia · AI assistant",
              des: "Sofia knows which modules each user can access and answers questions by combining sales, CRM, receivables and accounting data.",
              items: [
                "How is the business doing this month?",
                "Open opportunities vs. invoiced this month",
                "Profitability: income vs. expenses by line",
                "Top customers by revenue and pipeline",
                "Pending collections and projected cash flow",
                "Command center for administrators",
              ],
            },
          },
        },
        {
          title: "Helpdesk DataMind",
          tag: "Ticket management · Services",
          img: "img/projects/helpdesk-inicio.jpg",
          des: "Service desk for the whole company, not just IT: handle requests from any department (contracts, HR, finance, maintenance or customers) with a service catalog, SLAs, time tracking and reports.",
          modulesLabel: "Departments",
          modules: [
            "IT & systems",
            "Contracts & legal",
            "Human resources",
            "Finance & purchasing",
            "Maintenance",
            "Customer service",
          ],
          refs: [{ label: "Case study", url: "#" }],
          detail: {
            subtitle: "Ticket and service management for every department",
            overview:
              "Helpdesk DataMind brings the company's requests into a single portal. Each department publishes its services with its own categories and forms; users create tickets in three steps and follow their status. Teams work with priorities, SLA timers and time logs, and management tracks volume, workload, compliance and satisfaction with exportable reports. It works for internal teams and for serving client organizations.",
            stats: [
              { value: "SLA", label: "Assignment, review, response and resolution" },
              { value: "Hours", label: "Time tracking per ticket" },
              { value: "CSAT", label: "Satisfaction surveys" },
            ],
            gallery: [
              { img: "img/projects/helpdesk-inicio.jpg", caption: "Home: tickets by status, priority, SLA and satisfaction" },
              { img: "img/projects/helpdesk-tickets.jpg", caption: "My tickets with category, priority, status and SLA" },
              { img: "img/projects/helpdesk-nuevo-1.jpg", caption: "New ticket · step 1: choose the service" },
              { img: "img/projects/helpdesk-nuevo-2.jpg", caption: "New ticket · step 2: pick a category" },
              { img: "img/projects/helpdesk-nuevo-3.jpg", caption: "New ticket · step 3: category-specific form" },
              { img: "img/projects/helpdesk-reportes.jpg", caption: "Reports by status, priority and department, exportable to CSV" },
            ],
            audienceTitle: "For every department",
            audience: [
              "IT & systems",
              "Contract management",
              "Human resources",
              "Finance & purchasing",
              "Maintenance & facilities",
              "Customer service",
              "Projects & consulting",
            ],
            highlights: [
              "Service catalog per department, with categories and subcategories",
              "Dynamic forms per category, with attachments",
              "SLA timers for assignment, review, first response and resolution",
              "Time tracking and workload per team",
              "Portal for client organizations, each with its own tickets",
              "Knowledge base for self-service before opening a ticket",
            ],
            modulesTitle: "Features",
            modules: [
              { name: "Request portal", des: "Home with indicators and quick access to new tickets" },
              { name: "Tickets", des: "Tracking by status, priority, SLA and date" },
              { name: "Service catalog", des: "Services per department with categories and routing" },
              { name: "Forms", des: "Custom fields per category and evidence documents" },
              { name: "SLA", des: "Target times and due-date alerts" },
              { name: "Time tracking", des: "Time spent per ticket, agent and client" },
              { name: "Satisfaction", des: "CSAT surveys when a ticket is closed" },
              { name: "Knowledge base", des: "Self-service articles and guides" },
              { name: "Reports", des: "Volume, workload, SLA and CSAT by date range" },
              { name: "Organizations", des: "Clients and departments with their own users" },
            ],
            featured: {
              title: "Beyond IT",
              des: "The same flow works for any internal or customer request. Some examples:",
              items: [
                "Contracts: contract requests, reviews and renewals",
                "Hours: time logging and reporting per client or project",
                "Human resources: certificates, leave and onboarding",
                "Finance: payment approvals and purchase requests",
                "Maintenance: facility and equipment reports",
                "Customers: support with contractual SLAs",
              ],
            },
          },
        },
        {
          title: "Virtual Classroom",
          tag: "E-learning · Education",
          img: "img/projects/aula-virtual-curso.png",
          des: "Virtual classroom implementation for online training and education: courses with live sessions, recorded content, materials, activities and grades, branded for each institution.",
          modulesLabel: "Ideal for",
          modules: [
            "Corporate training",
            "Professional development",
            "Schools",
            "Universities",
            "Sports federations",
          ],
          refs: [{ label: "Case study", url: "#" }],
          detail: {
            subtitle: "Online learning platform branded for your institution",
            overview:
              "We implement and configure ready-to-run virtual classrooms: we brand the platform with the institution's identity, build the course structure, upload content, enroll participants and support the team during operations. Participants access their courses, calendar, materials and grades from any device.",
            stats: [
              { value: "96", label: "Participants in FESADA's first course" },
              { value: "24/7", label: "Access to content" },
              { value: "100%", label: "Online, from any device" },
            ],
            gallery: [
              {
                img: "img/projects/aula-virtual-curso.png",
                caption: "Course page with cover, description and calendar",
              },
              {
                img: "img/projects/aula-virtual-dashboard.png",
                caption: "Participant dashboard with courses and recent activity",
              },
              {
                img: "img/projects/aula-virtual-login.png",
                caption: "Branded login page for the institution",
              },
            ],
            audienceTitle: "Ideal for",
            audience: [
              "Corporate training",
              "Professional development",
              "Schools",
              "Universities",
              "Sports federations",
            ],
            highlights: [
              "Custom branding: logo, colors and dedicated login page",
              "Live sessions and recorded content",
              "Support materials and hands-on activities",
              "Grades and progress tracking for each participant",
              "Calendar of sessions and deadlines",
              "Notifications, messaging and dark mode",
            ],
            modulesTitle: "Features",
            modules: [
              { name: "Courses", des: "Cover, description, sections and categories" },
              { name: "Participants", des: "Enrollment, roles and groups" },
              { name: "Activities", des: "Assignments, quizzes, forums and resources" },
              { name: "Grades", des: "Gradebook and progress tracking" },
              { name: "Calendar", des: "Sessions, deadlines and course events" },
              { name: "Content bank", des: "Reusable resources across courses" },
              { name: "Files", des: "Documents, videos and presentations" },
              { name: "Messaging", des: "Notifications and internal messages" },
              { name: "Participant dashboard", des: "Courses, progress and recent items" },
              { name: "Administration", des: "Users, permissions, appearance and reports" },
            ],
            featured: {
              title: "Case: FESADA – Aquatic Educators Course",
              des: "For the Salvadoran Aquatic Sports Federation (FESADA), with support from INDES, we implemented the virtual classroom for the Aquatic Educators Course – Online No. 1 El Salvador 2026.",
              items: [
                "Classroom branded for FESADA with its own login",
                "96 participants enrolled in the first course",
                "Live sessions and recorded content",
                "Psycho-pedagogical foundations and swimming techniques",
                "Support materials and hands-on activities",
                "Program session calendar",
              ],
            },
          },
        },
        {
          title: "Intelligent ERP",
          tag: "Software · AI",
          img: "img/projects/erp-inteligente.png",
          des: "AI-powered enterprise resource planning system that integrates finance, inventory, sales and operations, with predictions and intelligent process automation.",
          refs: [{ label: "Case study", url: "#" }],
        },
        {
          title: "Omnichannel Agent",
          tag: "Conversational AI",
          img: "img/projects/agente-multicanal.png",
          des: "AI conversational assistant that serves your customers in a unified way across WhatsApp, web, social media and email, available 24/7 with contextual answers.",
          refs: [{ label: "Demo", url: "#" }],
        },
        {
          title: "AI Profiling",
          tag: "Data · AI",
          img: "img/projects/perfilamiento-ia.png",
          des: "Platform that analyzes and segments customer and user profiles using artificial intelligence to personalize offerings and anticipate behavior.",
          refs: [{ label: "Datasheet", url: "#" }],
        },
        {
          title: "Fixed Asset Management with RFID",
          tag: "RFID · Enterprise Software",
          img: "img/projects/activo-fijo-rfid.png",
          des: "Control and traceability of your company's asset lifecycle with real-time inventory monitoring via RFID tags: registration, location, automatic counts, depreciation, maintenance and disposal, reducing shrinkage and operational errors.",
          refs: [{ label: "Case study", url: "#" }],
        },
      ],
    },
    contact: {
      titleLight: "Let's talk about your",
      titleColored: "Project",
      address:
        "Avenida El Espino y Blvd Orden de Malta, Holcim Building Level 3 Office 4, Urb. Madre Selva III, El Salvador",
      namePlaceholder: "Name",
      emailPlaceholder: "Email",
      messagePlaceholder: "Message",
      send: "Send Message",
      sending: "Sending...",
      success: "Your message has been received, we will contact you soon.",
      required: "Please fill required fields",
      invalidEmail: "Please enter a valid email address",
      error:
        "We couldn't send your message. Write to ventas.sv@datamindss.com or reach us on WhatsApp.",
      rateLimit: "You've sent several messages. Please wait a moment and try again.",
      whatsapp: "Message us on WhatsApp",
      whatsappNumber: "+503 7468 9732",
      whatsappMessage:
        "Hi! I'm coming from the DataMind website and I'd like information about your services.",
    },
    footer: {
      tagline:
        "Enterprise solutions in software, artificial intelligence, data, infrastructure and RFID.",
      addressShort: "Holcim Building, Level 3 Office 4 · Urb. Madre Selva III, El Salvador",
      servicesTitle: "Services",
      solutionsTitle: "Solutions",
      companyTitle: "DataMind",
      contactTitle: "Contact",
      talk: "Let's talk",
      ctaTitle: "Have a project in mind?",
      ctaText: "Let's talk about your process, your data and the result you need.",
      ctaButton: "Contact DataMind",
      rights: "All rights reserved.",
      backToTop: "Back to top",
    },
    sofia: {
      bubble: "Chat with us",
      role: "DataMind Assistant",
      greeting:
        "Hi! I'm Sofia, from the DataMind team. How can I help you? I can tell you about our software, data and artificial intelligence services.",
      placeholder: "Type your message...",
      fallback: "Thanks for your message, we'll get back to you shortly.",
      error: "I couldn't connect right now. Please try again.",
      rateLimit:
        "You've sent too many messages in a row. Please wait a moment and try again.",
    },
  },
};

// Idioma según la configuración del navegador (instantáneo, sin red)
function localeFromNavigator() {
  const lang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  return lang.startsWith("es") ? "es" : "en";
}

// Idioma inicial: elección guardada del usuario > idioma del navegador
function initialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "es" || saved === "en") return saved;
  } catch (e) {
    // localStorage no disponible
  }
  return localeFromNavigator();
}

export const i18n = reactive({
  locale: initialLocale(),

  // Mensajes del idioma activo
  get m() {
    return messages[this.locale] || messages.es;
  },

  // Cambio manual: se recuerda y tiene prioridad sobre la detección
  setLocale(locale) {
    if (locale !== "es" && locale !== "en") return;
    this.locale = locale;
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch (e) {
      // ignorar
    }
  },
});

/**
 * Detección por zona geográfica (IP). Si el usuario ya eligió idioma
 * manualmente, se respeta su elección y no se hace nada.
 * Si la API falla o tarda, se queda el idioma del navegador.
 */
export async function autoDetectLocale() {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return; // el usuario ya eligió
  } catch (e) {
    // sigue con la detección
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);

    const res = await fetch("https://get.geojs.io/v1/ip/country.json", {
      signal: controller.signal,
    });
    clearTimeout(timeout);

    const data = await res.json();
    const country = (data.country || "").toUpperCase();
    if (!country) return;

    const locale = SPANISH_COUNTRIES.includes(country) ? "es" : "en";
    i18n.locale = locale;
    document.documentElement.lang = locale;
  } catch (e) {
    // Sin red / API caída / bloqueada -> se mantiene el idioma del navegador
  }
}

document.documentElement.lang = i18n.locale;
